import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useReportingChatStore } from "../store";

const MAX_INPUT_LENGTH = 4000;

export const ChatInput: React.FC<{
	sendQuery: (query: string) => Promise<void>;
	isStreaming: boolean;
	// True while a reopened artifact's GET is in flight — blocks sending independently of
	// isStreaming, which drives this component's own "assistant is responding" semantics (refocus,
	// thinking state) and must not be conflated with this artifact-load gate.
	isDisabled?: boolean;
}> = ({ sendQuery, isStreaming, isDisabled = false }) => {
	const inputValue = useReportingChatStore((state) => state.inputValue);
	const setInputValue = useReportingChatStore((state) => state.setInputValue);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const wasStreamingRef = useRef(false);

	const sendBlocked = isStreaming || isDisabled;
	const canSend = inputValue.trim().length > 0 && !sendBlocked;

	const handleSubmit = useCallback(
		(e?: React.FormEvent) => {
			e?.preventDefault();
			if (!canSend) return;
			sendQuery(inputValue.trim());
		},
		[canSend, inputValue, sendQuery]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSubmit();
			}
		},
		[handleSubmit]
	);

	useLayoutEffect(() => {
		const textarea = textareaRef.current;
		if (textarea) {
			textarea.style.height = "auto";
			textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
		}
	}, [inputValue]);

	useEffect(() => {
		textareaRef.current?.focus();
	}, []);

	useEffect(() => {
		if (wasStreamingRef.current && !isStreaming) textareaRef.current?.focus();
		wasStreamingRef.current = isStreaming;
	}, [isStreaming]);

	return (
		<div className="flex flex-col gap-2 border-t border-neutral-200 px-4 pt-3 pb-4">
			<form onSubmit={handleSubmit} className="mx-auto box-border flex w-full max-w-3xl items-end gap-2">
				<textarea
					ref={textareaRef}
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder="Ask about your review, goal, survey, or employee data…"
					disabled={sendBlocked}
					className="box-border h-[40px] max-h-[160px] min-h-[40px] w-full resize-none overflow-hidden rounded border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 hover:overflow-y-auto focus:overflow-y-auto focus:border-transparent focus:ring-2 focus:ring-primary-5 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-neutral-50"
					rows={1}
					maxLength={MAX_INPUT_LENGTH}
					aria-label="Ask a reporting question"
				/>
				<button
					type="submit"
					disabled={!canSend}
					aria-label="Send message"
					className={`flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded border-none transition-all ${
						canSend
							? "cursor-pointer bg-primary-5 hover:bg-primary-7 active:bg-primary-8"
							: "cursor-not-allowed bg-neutral-300"
					}`}
				>
					<FontAwesomeIcon icon={faPaperPlane} className="text-base text-white" />
				</button>
			</form>
			<p className="w-full text-center text-xs text-neutral-400">
				AI may make mistakes. Please verify important information.
			</p>
		</div>
	);
};

export default ChatInput;
